import { lazy, Suspense, useState, type ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "next-themes";

const DashboardTileVisual = lazy(() =>
  import("./LoginTileVisuals").then((module) => ({ default: module.DashboardTileVisual }))
);
const BillingTileVisual = lazy(() =>
  import("./LoginTileVisuals").then((module) => ({ default: module.BillingTileVisual }))
);
const UsageTileVisual = lazy(() =>
  import("./LoginTileVisuals").then((module) => ({ default: module.UsageTileVisual }))
);

function tileInk(resolvedTheme: string | undefined): string {
  return resolvedTheme === "light" ? "#059669" : "#00FFA3";
}

const DEFAULT_ACTIVE_INDEX = "02";

function Tile({
  index,
  title,
  description,
  active,
  span,
  Visual,
  ink,
  onActivate,
}: {
  index: string;
  title: string;
  description: string;
  active: boolean;
  span?: boolean;
  Visual: ComponentType<{ ink: string }>;
  ink: string;
  onActivate: () => void;
}) {
  return (
    <article
      className={[
        "login-rise relative isolate overflow-hidden min-h-[220px] md:min-h-0 rounded-2xl p-7 flex flex-col justify-end",
        "cursor-pointer transition-colors duration-200 ease-out border",
        span ? "sm:col-span-2" : "",
        active
          ? "border-[var(--lg-mint)]/50 bg-[var(--lg-mint-dim)]"
          : "border-[var(--lg-line)] bg-[var(--lg-card)]",
      ].join(" ")}
      style={{ animationDelay: `${Number(index) * 90}ms` }}
      onPointerEnter={onActivate}
      onFocus={onActivate}
    >
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <Suspense fallback={null}>
          <Visual ink={ink} />
        </Suspense>
      </div>
      {span ? (
        <p
          aria-hidden
          className="login-mark login-display pointer-events-none absolute -bottom-5 -left-3 z-[1] select-none text-[min(28vw,15rem)] font-extrabold leading-none"
        >
          GEL
        </p>
      ) : null}
      <span className="login-mono relative z-10 mb-3 text-[13px] tracking-[0.2em] text-[var(--lg-mint)]">
        {index}
      </span>
      <h3 className="login-display relative z-10 m-0 text-[1.95rem] font-extrabold leading-[1.05] tracking-tight md:text-[2.2rem]">
        {title}
      </h3>
      <p className="relative z-10 mt-3.5 mb-0 max-w-xl text-[15px] leading-relaxed text-[var(--lg-mute)] md:text-base">
        {description}
      </p>
    </article>
  );
}

export function LoginCapabilityWall() {
  const { t } = useTranslation();
  const { resolvedTheme } = useTheme();
  const ink = tileInk(resolvedTheme);
  const [activeIndex, setActiveIndex] = useState(DEFAULT_ACTIVE_INDEX);

  return (
    <aside
      className="relative h-full min-h-0 grid grid-cols-1 sm:grid-cols-2 md:grid-rows-2 gap-3"
      onPointerLeave={() => setActiveIndex(DEFAULT_ACTIVE_INDEX)}
    >
      <Tile
        index="01"
        title={t("login.tiles.dashboardTitle")}
        description={t("login.tiles.dashboardDesc")}
        active={activeIndex === "01"}
        Visual={DashboardTileVisual}
        ink={ink}
        onActivate={() => setActiveIndex("01")}
      />
      <Tile
        index="02"
        title={t("login.tiles.billingTitle")}
        description={t("login.tiles.billingDesc")}
        active={activeIndex === "02"}
        Visual={BillingTileVisual}
        ink={ink}
        onActivate={() => setActiveIndex("02")}
      />
      <Tile
        index="03"
        title={t("login.tiles.usageTitle")}
        description={t("login.tiles.usageDesc")}
        active={activeIndex === "03"}
        span
        Visual={UsageTileVisual}
        ink={ink}
        onActivate={() => setActiveIndex("03")}
      />
    </aside>
  );
}
